/**
 * Tunes Component - Musical Notation Pages Section
 * 
 * Author: Markus van Kempen - markus.van.kempen@gmail.com
 * Last Updated: October 2025
 * Version: 1.0.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from './ToastContainer';
import { useAuth } from '../context/AuthContext';
import { getIcon } from '../utils/icons';
import { decodeHtmlEntities } from '../utils/htmlDecode';
import LoadingSpinner from './LoadingSpinner';
import PaginationBar from './PaginationBar';
import SEO from './SEO';
import { parseMultipleTunes, mapTunesToBulkImportEntries } from '../utils/abcBulkImport';
import { formatAlternateTitles } from '../utils/tuneAlternateTitles';
import TuneFormatMarker from './TuneFormatMarker';
import RegisterNudgeBubble from './RegisterNudgeBubble';
import {
  collectTuneAudioIds,
  collectTuneNotationAssetIds,
  tuneNotationLegacyImageUrl,
} from '../utils/tunePageMedia';
import './Tunes.css';

/** sessionStorage key for tunes search/filter state */
const TUNES_STATE_KEY = 'tunes_filterState';

const VALID_PAGE_TYPES = new Set([
  'all',
  'music-notation',
  'abc-asset',
  'book-index',
  'cd-index',
  'tune-notation',
]);

/** Map ?type= / ?abc= URL params to tunes page type filter (abc → abc-asset). */
function parseTunesTypeFromSearchParams(searchParams) {
  if (!searchParams) return null;
  const abcFlag = (searchParams.get('abc') || '').trim().toLowerCase();
  if (abcFlag === '1' || abcFlag === 'true' || abcFlag === 'yes') {
    return 'abc-asset';
  }
  const raw = (searchParams.get('type') || '').trim().toLowerCase();
  if (!raw) return null;
  if (raw === 'abc' || raw === 'abc-tune' || raw === 'abc-tunes') return 'abc-asset';
  return VALID_PAGE_TYPES.has(raw) ? raw : null;
}

/** Read persisted filter state (returns defaults if nothing saved) */
function loadTunesFilterState() {
  try {
    const raw = sessionStorage.getItem(TUNES_STATE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) { /* ignore */ }
  return null;
}

/** Admin visibility for /tunes: public (default), include hidden, or hidden only */
function normalizeHiddenTunesMode(saved) {
  if (saved === 'only' || saved === 'include') return saved;
  if (saved === true || saved === 'true') return 'include'; // legacy sessionStorage
  return 'public';
}

function tuneMediaFlagsFromMetadata(metadata, hasAudioFromApi = false) {
  const hasNotation =
    collectTuneNotationAssetIds(metadata).length > 0 ||
    !!tuneNotationLegacyImageUrl(metadata) ||
    !!(metadata.notation_asset_filenames && String(metadata.notation_asset_filenames).trim());
  const hasAudio =
    hasAudioFromApi ||
    collectTuneAudioIds(metadata).length > 0 ||
    !!(metadata.mp3_asset_id) ||
    !!(metadata.audio_asset_filenames && String(metadata.audio_asset_filenames).trim());
  return { hasAudio, hasNotation };
}

const Tunes = () => {
  const { showError, showSuccess } = useToast();
  const { currentUser, hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasFilterQuery = ['type', 'q', 'search', 'region', 'artist', 'page'].some(
    (key) => String(searchParams.get(key) || '').trim() !== ''
  );

  // Restore persisted filter state (session-scoped — cleared when tab closes)
  const _saved = loadTunesFilterState();
  const _urlPageType = parseTunesTypeFromSearchParams(searchParams);

  const [searchTerm, setSearchTerm] = useState(() => {
    const fromUrl = (searchParams.get('q') || '').trim();
    if (fromUrl) return fromUrl;
    return _saved?.searchTerm ?? '';
  });
  const [selectedRegions, setSelectedRegions] = useState(_saved?.selectedRegions ?? []);
  const [selectedTuneTypes, setSelectedTuneTypes] = useState(_saved?.selectedTuneTypes ?? []);
  const [selectedArtists, setSelectedArtists] = useState(_saved?.selectedArtists ?? []);
  const [showABCOnly, setShowABCOnly] = useState(() => {
    if (_urlPageType === 'abc-asset') return true;
    return _saved?.showABCOnly ?? false;
  });
  const [selectedPageType, setSelectedPageType] = useState(() => {
    if (_urlPageType) return _urlPageType;
    return _saved?.selectedPageType ?? 'all';
  }); // 'all', 'tune-notation', 'abc-asset', 'book-index'
  const [hiddenTunesMode, setHiddenTunesMode] = useState(() =>
    normalizeHiddenTunesMode(_saved?.hiddenTunesMode ?? _saved?.showHiddenTunes)
  );
  const [tunesContent, setTunesContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(_saved?.currentPage ?? 1);
  const [tunesPerPage, setTunesPerPage] = useState(() => {
    const saved = localStorage.getItem('tunesPerPage');
    const n = saved ? parseInt(saved, 10) : 25;
    return [25, 50, 75].includes(n) ? n : 25;
  });

  // Bulk import state
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportTunes, setBulkImportTunes] = useState([]);
  const [bulkImportHeader, setBulkImportHeader] = useState('');
  const [bulkImportProgress, setBulkImportProgress] = useState({ current: 0, total: 0, status: '' });
  const [uploading, setUploading] = useState(false);
  const bulkImportFileInputRef = useRef(null);
  const listTopRef = useRef(null);
  const shouldScrollToTopRef = useRef(false);

  // URL Import state
  const [showImportUrlModal, setShowImportUrlModal] = useState(false);
  const [importTuneUrl, setImportTuneUrl] = useState('');
  const [isUrlImporting, setIsUrlImporting] = useState(false);

  // Check if user can create tunes
  const canCreate = currentUser && hasAnyRole && hasAnyRole(['admin', 'super_admin', 'content_creator']);
  // Check if user is admin (for bulk import + hidden tune filter)
  const isAdmin = currentUser && hasAnyRole && hasAnyRole(['admin', 'super_admin']);

  const handleCreateNewTune = () => {
    // Generate a unique pageId for the new tune
    const timestamp = Date.now();
    const pageId = `new-tune-${timestamp}`;
    navigate(`/admin/pages/edit?pageId=${pageId}&type=tune-notation`);
  };

  const handleTuneUrlImport = async (e) => {
    e.preventDefault();
    if (!importTuneUrl) return;

    setIsUrlImporting(true);
    try {
      const response = await fetch('/api/content/import/tune-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('token')
        },
        body: JSON.stringify({ url: importTuneUrl }),
      });

      const data = await response.json();

      if (data.success) {
        if (data.templateApplied === false) {
          showSuccess(`Tune imported (raw HTML — seed the tune-page template in Admin → Page Templates to use the managed layout).`);
        } else {
          showSuccess(`Tune imported with the admin tune-page template! Check "Notation" tab.`);
        }
        setShowImportUrlModal(false);
        setImportTuneUrl('');
        loadTunesContent();
        // Optional: navigate to the new page
        // navigate(/page/${data.pageId});
      } else {
        showError(data.error || 'Failed to import tune');
      }
    } catch (error) {
      showError('Error importing tune: ' + error.message);
    } finally {
      setIsUrlImporting(false);
    }
  };

  // Region categories
  const regions = [
    { id: 'scottish', name: 'Scottish', icon: '🏴󠁧󠁢󠁳󠁣󠁴󠁿' },
    { id: 'irish', name: 'Irish', icon: '🇮🇪' },
    { id: 'cape-breton', name: 'Cape Breton', icon: '🎻' }
  ];

  // Tune type categories
  const tuneTypes = [
    { id: 'reel', name: 'Reel', icon: '🎼' },
    { id: 'jig', name: 'Jig', icon: '🎵' },
    { id: 'strathspey', name: 'Strathspey', icon: '🎶' },
    { id: 'march', name: 'March', icon: '🥁' },
    { id: 'hornpipe', name: 'Hornpipe', icon: '🎺' },
    { id: 'waltz', name: 'Waltz', icon: '💃' },
    { id: 'polka', name: 'Polka', icon: '🎪' },
    { id: 'air', name: 'Air/Slow', icon: '🎹' }
  ];

  const pageTypes = [
    { id: 'all', name: 'All Types', icon: '📄' },
    { id: 'music-notation', name: 'Music & Notation', icon: '🎼' },
    { id: 'abc-asset', name: 'ABC', icon: '🎵' },
    { id: 'book-index', name: 'Book Index', icon: '📖' },
    { id: 'cd-index', name: 'CD Tracks Index', icon: '💿' }
  ];

  const getAvailableRegions = () => {
    let typeFilteredContent = tunesContent;
    if (selectedPageType === 'music-notation') {
      typeFilteredContent = typeFilteredContent.filter(item => item.type === 'tune-notation' || item.type === 'abc-asset');
    } else if (selectedPageType !== 'all') {
      typeFilteredContent = typeFilteredContent.filter(item => item.type === selectedPageType);
    }

    if (selectedTuneTypes.length > 0) {
      typeFilteredContent = typeFilteredContent.filter(item => {
        if (!item.tuneType) return false;
        const itemTuneType = item.tuneType.toLowerCase();
        return selectedTuneTypes.some(type => itemTuneType.includes(type));
      });
    }

    const availableIds = new Set(typeFilteredContent.map(item => item.category));
    return regions.filter(r => availableIds.has(r.id));
  };

  const getAvailableTuneTypes = () => {
    let typeFilteredContent = tunesContent;
    if (selectedPageType === 'music-notation') {
      typeFilteredContent = typeFilteredContent.filter(item => item.type === 'tune-notation' || item.type === 'abc-asset');
    } else if (selectedPageType !== 'all') {
      typeFilteredContent = typeFilteredContent.filter(item => item.type === selectedPageType);
    }

    if (selectedRegions.length > 0) {
      typeFilteredContent = typeFilteredContent.filter(item => selectedRegions.includes(item.category));
    }

    return tuneTypes.filter(type => {
      return typeFilteredContent.some(item => {
        if (!item.tuneType) return false;
        return item.tuneType.toLowerCase().includes(type.id);
      });
    });
  };

  const getAvailableArtists = () => {
    let typeFilteredContent = tunesContent;
    if (selectedPageType === 'music-notation') {
      typeFilteredContent = typeFilteredContent.filter(item => item.type === 'tune-notation' || item.type === 'abc-asset');
    } else if (selectedPageType !== 'all') {
      typeFilteredContent = typeFilteredContent.filter(item => item.type === selectedPageType);
    }

    if (selectedRegions.length > 0) {
      typeFilteredContent = typeFilteredContent.filter(item => selectedRegions.includes(item.category));
    }

    if (selectedTuneTypes.length > 0) {
      typeFilteredContent = typeFilteredContent.filter(item => {
        if (!item.tuneType) return false;
        const itemTuneType = item.tuneType.toLowerCase();
        return selectedTuneTypes.some(type => itemTuneType.includes(type));
      });
    }

    const artists = new Set();
    typeFilteredContent.forEach(item => {
      let finalArtist = '';
      if (typeof item.artist === 'string') {
        finalArtist = item.artist.trim();
      } else if (Array.isArray(item.artist) && item.artist.length > 0) {
        finalArtist = item.artist[0].trim();
      }
      
      if (finalArtist && finalArtist.toLowerCase() !== 'unknown') {
        artists.add(finalArtist);
      }
    });
    return Array.from(artists).sort();
  };

  useEffect(() => {
    loadTunesContent();

    // Reload tunes when user navigates back to this page
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadTunesContent({ soft: true });
      }
    };

    const handleWindowFocus = () => loadTunesContent({ soft: true });

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, hiddenTunesMode, isAdmin]);

  // Admins only: turning off admin session clears hidden-tune filter
  useEffect(() => {
    if (!isAdmin && hiddenTunesMode !== 'public') {
      setHiddenTunesMode('public');
    }
  }, [isAdmin, hiddenTunesMode]);

  // Persist search/filter state to sessionStorage so Back restores the results
  useEffect(() => {
    try {
      sessionStorage.setItem(TUNES_STATE_KEY, JSON.stringify({
        searchTerm,
        selectedRegions,
        selectedTuneTypes,
        selectedArtists,
        showABCOnly,
        selectedPageType,
        hiddenTunesMode: isAdmin ? hiddenTunesMode : 'public',
        currentPage,
      }));
    } catch (_) { /* storage unavailable — silently skip */ }
  }, [searchTerm, selectedRegions, selectedTuneTypes, selectedArtists, showABCOnly, selectedPageType, hiddenTunesMode, isAdmin, currentPage]);

  // Scroll list header into view after pagination / page-size changes (after DOM updates)
  useEffect(() => {
    if (!shouldScrollToTopRef.current) return;
    shouldScrollToTopRef.current = false;
    requestAnimationFrame(() => {
      listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [currentPage, tunesPerPage]);

  // Deep link / history: ?q= applies the tune list text filter (same as the search box)
  useEffect(() => {
    const q = (searchParams.get('q') || '').trim();
    if (q) {
      setSearchTerm(q);
      setCurrentPage(1);
    }
  }, [searchParams]);

  // Deep link: ?type=abc or ?type=abc-asset filters the Type dropdown to ABC tunes
  useEffect(() => {
    const urlType = parseTunesTypeFromSearchParams(searchParams);
    if (!urlType) return;
    setSelectedPageType(urlType);
    setShowABCOnly(urlType === 'abc-asset');
    setCurrentPage(1);
  }, [searchParams]);

  const loadTunesContent = async (options = {}) => {
    const soft = options.soft === true;
    const mode = options.hiddenTunesMode ?? hiddenTunesMode;
    const useHiddenApi = isAdmin && (mode === 'include' || mode === 'only');
    try {
      if (!soft) {
        setLoading(true);
      }

      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (mode === 'include') params.set('includeHidden', '1');
      if (mode === 'only') params.set('hiddenOnly', '1');
      const qs = params.toString();
      const tunesUrl = qs ? `/api/content/tunes?${qs}` : '/api/content/tunes';
      const authHeaders = useHiddenApi && token
        ? { Authorization: `Bearer ${token}` }
        : {};

      // Fetch both tune pages and ABC assets
      const [pagesResponse, assetsResponse] = await Promise.all([
        fetch(tunesUrl, {
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          }
        }),
        mode === 'only'
          ? Promise.resolve({ ok: false })
          : fetch('/api/assets?category=ABC-Tune&is_active=1&limit=2000', {
              headers: {
                'Content-Type': 'application/json'
              }
            })
      ]);

      if (!pagesResponse.ok) {
        const errorText = await pagesResponse.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to load tunes: ${pagesResponse.status} ${pagesResponse.statusText}`);
      }

      const contentType = pagesResponse.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await pagesResponse.text();
        console.error('Non-JSON response:', text.substring(0, 500));
        throw new Error('Server returned non-JSON response');
      }

      const pages = await pagesResponse.json();
      let abcAssets = [];

      // Process ABC assets if available
      if (assetsResponse.ok) {
        try {
          const assetsData = await assetsResponse.json();
          abcAssets = (assetsData.assets || []).map(asset => {
            // Parse ABC metadata from description
            let metadata = {};
            try {
              if (asset.description) {
                const abcMatch = asset.description.match(/\[ABC_METADATA\](.*?)\[\/ABC_METADATA\]/s);
                if (abcMatch) {
                  metadata = JSON.parse(abcMatch[1]);
                }
              }
            } catch (e) {
              console.error('Error parsing ABC metadata:', e);
            }

            // Extract description without metadata
            const cleanDescription = asset.description
              ? asset.description.replace(/\[ABC_METADATA\].*?\[\/ABC_METADATA\]/s, '').trim()
              : '';

            // Determine category from metadata
            let category = 'scottish';
            if (metadata.origin_region) {
              const origin = metadata.origin_region.toLowerCase();
              if (origin.includes('irish')) {
                category = 'irish';
              } else if (origin.includes('cape breton')) {
                category = 'cape-breton';
              }
            }

            return {
              id: `asset-${asset.id}`,
              title: metadata.tune_title || asset.original_filename || 'Untitled Tune',
              category: category,
              type: 'abc-asset',
              description: cleanDescription || asset.description || 'ABC notation tune',
              url: `/tune-asset/${asset.id}`,
              artist: metadata.composer_arranger || '',
              tuneType: metadata.rhythm_style || '',
              key: metadata.key_signature || metadata.key || '',
              timeSignature: '',
              source: metadata.source_album || '',
              recordingAlbum: '',
              collectionBook: '',
              alsoKnownAs: formatAlternateTitles(metadata),
              recordingYear: '',
              tags: asset.tags || '',
              image: null,
              featured: asset.featured === true || asset.featured === 1,
              year: new Date(asset.updated_at || asset.created_at).getFullYear()
            };
          });
        } catch (e) {
          console.error('Error processing ABC assets:', e);
        }
      }

      // Transform pages to tune format
      const processedContent = pages.map(page => {
        // Parse metadata if it exists
        let metadata = {};
        try {
          if (page.metadata) {
            metadata = typeof page.metadata === 'string' ? JSON.parse(page.metadata) : page.metadata;
          }
        } catch (e) {
          console.error('Error parsing metadata for page:', page.page_id, e);
        }

        // Get description for use in multiple places
        const desc = (page.description || '').toLowerCase();

        // Normalize page_type for consistent comparison (handle null/undefined and case-insensitive)
        const pageType = (page.page_type || '').toLowerCase().trim();

        // Determine category from page type or metadata
        let category = 'scottish';
        if (pageType === 'book-index') {
          category = 'book-index';
        } else if (pageType === 'cd-index') {
          category = 'cd-index';
        } else {
          category = (metadata.region && typeof metadata.region === 'string')
            ? metadata.region.toLowerCase().replace(' ', '-')
            : 'scottish';
          if (!metadata.region || typeof metadata.region !== 'string') {
            if (desc.includes('irish') || page.page_id.includes('irish')) {
              category = 'irish';
            } else if (desc.includes('cape breton') || page.page_id.includes('cape')) {
              category = 'cape-breton';
            }
          }
        }

        // Extract artist from metadata or description
        let artist = metadata.artist || '';
        if (!artist) {
          const artistMatch = desc.match(/recorded by ([^.]+)|performed by ([^.]+)|from ([^.]+\d{2,4})|by ([^.]+)/i);
          if (artistMatch) {
            artist = artistMatch[1] || artistMatch[2] || artistMatch[3] || artistMatch[4];
            artist = artist.split(/on |,|the cd|album/i)[0].trim();
            artist = artist.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          }
        }

        // Determine type based on page_type (reuse pageType from above)
        let itemType = 'tune-notation';
        if (pageType === 'book-index') {
          itemType = 'book-index';
        } else if (pageType === 'cd-index') {
          itemType = 'cd-index';
        } else if (pageType === 'abc-notation' || pageType === 'abc-asset') {
          itemType = 'abc-asset';
        }

        const mediaFlags = tuneMediaFlagsFromMetadata(metadata, page.has_audio === 1);

        return {
          id: page.page_id,
          title: page.title || page.page_id,
          category: category,
          type: itemType,
          description: page.description || 'Traditional fiddle tune',
          url: `/page/${page.page_id}`,
          artist: artist,
          tuneType: metadata.tune_type || '',
          key: metadata.key_signature || metadata.key || '',
          timeSignature: metadata.time_signature || '',
          source: metadata.source || '',
          recordingAlbum: metadata.recording_album || '',
          collectionBook: metadata.collection_book || '',
          alsoKnownAs: formatAlternateTitles(metadata),
          recordingYear: metadata.recording_year || '',
          tags: metadata.tags || '',
          image: null,
          hasAudio: mediaFlags.hasAudio,
          hasNotation: mediaFlags.hasNotation,
          featured: page.featured === 1 || page.featured === true,
          isHidden: page.is_hidden === 1 || page.is_hidden === true,
          year: metadata.recording_year || new Date(page.updated_at).getFullYear()
        };
      });

      // Combine pages and ABC assets
      const allContent = [...processedContent, ...abcAssets];

      // Sort featured items first, then alphabetically by title
      allContent.sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();
        return titleA.localeCompare(titleB);
      });

      setTunesContent(allContent);
      setFilteredContent(allContent);
    } catch (error) {
      console.error('Error loading tunes content:', error);
      showError(`Failed to load tunes: ${error.message}`);
      setTunesContent([]);
      setFilteredContent([]);
    } finally {
      if (!soft) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Filter content based on search term, regions, tune types, ABC filter, and page type
    let filtered = tunesContent;

    // Filter by page type (dropdown)
    if (selectedPageType === 'music-notation') {
      filtered = filtered.filter(item => item.type === 'tune-notation' || item.type === 'abc-asset');
    } else if (selectedPageType !== 'all') {
      filtered = filtered.filter(item => item.type === selectedPageType);
    }

    // Filter by ABC only (legacy - kept for backwards compatibility)
    if (showABCOnly && selectedPageType === 'all') {
      filtered = filtered.filter(item => item.type === 'abc-asset');
    }

    // Filter by regions (multi-select)
    if (selectedRegions.length > 0) {
      filtered = filtered.filter(item => selectedRegions.includes(item.category));
    }

    // Filter by tune types (multi-select)
    if (selectedTuneTypes.length > 0) {
      filtered = filtered.filter(item => {
        if (!item.tuneType) return false;
        const itemTuneType = item.tuneType.toLowerCase();
        return selectedTuneTypes.some(type => itemTuneType.includes(type));
      });
    }

    // Filter by artists
    if (selectedArtists.length > 0) {
      filtered = filtered.filter(item => {
        if (!item.artist) return false;
        const artistStr = Array.isArray(item.artist) ? item.artist.join(',').toLowerCase() : String(item.artist).toLowerCase();
        return selectedArtists.some(artist => artistStr.includes(artist.toLowerCase()));
      });
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        // Helper function to safely convert to string and search
        const safeSearch = (value) => {
          if (!value) return false;
          if (typeof value === 'string') return value.toLowerCase().includes(search);
          if (Array.isArray(value)) return value.some(v => v && v.toString().toLowerCase().includes(search));
          return value.toString().toLowerCase().includes(search);
        };

        return item.title.toLowerCase().includes(search) ||
          item.description.toLowerCase().includes(search) ||
          safeSearch(item.artist) ||
          safeSearch(item.tuneType) ||
          safeSearch(item.tags) ||
          safeSearch(item.source) ||
          safeSearch(item.recordingAlbum) ||
          safeSearch(item.collectionBook) ||
          safeSearch(item.alsoKnownAs);
      });
    }

    // Sort alphabetically by title
    filtered.sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      const titleA = (a.title || '').toLowerCase();
      const titleB = (b.title || '').toLowerCase();
      return titleA.localeCompare(titleB);
    });

    setFilteredContent(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, selectedRegions, selectedTuneTypes, selectedArtists, showABCOnly, selectedPageType, tunesContent]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearAllFilters = () => {
    setSelectedRegions([]);
    setSelectedTuneTypes([]);
    setSelectedArtists([]);
    setShowABCOnly(false);
    setSearchTerm('');
    setSelectedPageType('all');
    setHiddenTunesMode('public');
  };

  // Open the same upload flow as Admin → Assets (single tune from bulk list)
  const handleImportTuneFromBulk = (tune) => {
    setShowBulkImportModal(false);
    setBulkImportTunes([]);
    setBulkImportHeader('');
    navigate('/admin/assets', {
      state: {
        pendingAbcBulkTune: {
          fullContent: tune.fullContent,
          filename: tune.filename,
          tags: tune.tags,
          description: tune.description,
          metadata: tune.metadata,
          title: tune.title
        }
      }
    });
  };

  // Handle bulk ABC file import
  const handleBulkABCImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.abc') && !fileName.endsWith('.txt')) {
      showError('Please select an ABC or TXT file');
      return;
    }

    try {
      const fileText = await file.text();
      console.log('Parsing ABC file, length:', fileText.length);
      const { tunes, header } = parseMultipleTunes(fileText);

      console.log(`Parsed ${tunes.length} tune(s) from file`);
      console.log('Header length:', header.length);
      console.log('First tune:', tunes[0] ? { title: tunes[0].fields.T, fields: Object.keys(tunes[0].fields) } : 'none');

      if (tunes.length === 0) {
        showError('No tunes found in file. Make sure the file contains ABC notation with X: fields.');
        return;
      }

      const processedTunes = mapTunesToBulkImportEntries(tunes);

      // Check which tunes already exist
      try {
        const filenames = processedTunes.map(t => t.filename).join(',');
        const checkResponse = await fetch(`/api/assets/check-exists?filenames=${encodeURIComponent(filenames)}`, {
          headers: {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
          }
        });

        if (checkResponse.ok) {
          const existingAssets = await checkResponse.json();

          // Mark tunes that already exist
          processedTunes.forEach(tune => {
            if (existingAssets[tune.filename]) {
              tune.exists = true;
              tune.existingAssetId = existingAssets[tune.filename].id;
            }
          });
        }
      } catch (error) {
        console.error('Error checking existing assets:', error);
        // Continue anyway - we'll just not mark existing ones
      }

      setBulkImportHeader(header);
      setBulkImportTunes(processedTunes);
      setShowBulkImportModal(true);
    } catch (error) {
      console.error('Error parsing ABC file:', error);
      showError('Failed to parse ABC file: ' + error.message);
    }

    // Reset file input
    e.target.value = '';
  };

  // Import all tunes from bulk import modal (upload all at once)
  const handleImportAllTunes = async () => {
    if (bulkImportTunes.length === 0) return;

    // Filter out tunes that already exist (unless user wants to update them)
    const tunesToImport = bulkImportTunes.filter(tune => !tune.exists);
    const existingTunes = bulkImportTunes.filter(tune => tune.exists);

    if (existingTunes.length > 0) {
      const skipExisting = window.confirm(
        `${existingTunes.length} tune(s) already exist and will be skipped. ` +
        `Do you want to continue importing the remaining ${tunesToImport.length} tune(s)?`
      );
      if (!skipExisting) {
        return;
      }
    }

    if (tunesToImport.length === 0) {
      showError('All tunes already exist. No new tunes to import.');
      return;
    }

    setBulkImportProgress({ current: 0, total: tunesToImport.length, status: 'Starting import...' });
    setUploading(true);

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    try {
      for (let i = 0; i < tunesToImport.length; i++) {
        const tune = tunesToImport[i];
        setBulkImportProgress({
          current: i + 1,
          total: tunesToImport.length,
          status: `Uploading: ${tune.title}...`
        });

        try {
          // Create a Blob from the full content (header + tune)
          const blob = new Blob([tune.fullContent], { type: 'text/plain' });
          const file = new File([blob], tune.filename, { type: 'text/plain' });

          const formData = new FormData();
          formData.append('file', file);
          formData.append('category', 'ABC-Tune');
          formData.append('description', tune.description);
          formData.append('tags', tune.tags);
          formData.append('is_public', 'true');

          const response = await fetch('/api/assets', {
            method: 'POST',
            headers: {
              'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: formData
          });

          if (!response.ok) {
            let errorMessage = `Upload failed (HTTP ${response.status})`;
            try {
              const errorText = await response.text();
              try {
                const errorData = JSON.parse(errorText);
                errorMessage = errorData.message || errorData.error || errorMessage;
                if (response.status === 409 && errorData.existingId) {
                  errorMessage = `Duplicate detected: ${errorMessage}`;
                }
              } catch (e) {
                errorMessage = errorText || errorMessage;
              }
            } catch (e) {
              errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(errorMessage);
          }

          const result = await response.json();
          console.log(`Successfully imported: ${tune.title} (ID: ${result.id})`);
          successCount++;
        } catch (error) {
          errorCount++;
          const errorMsg = error.message || 'Unknown error';
          errors.push({ tune: tune.title, error: errorMsg });
          console.error(`Error importing ${tune.title}:`, error);
          console.error(`Failed to import "${tune.title}": ${errorMsg}`);
        }
      }

      setBulkImportProgress({
        current: bulkImportTunes.length,
        total: bulkImportTunes.length,
        status: 'Import complete!'
      });

      if (errorCount > 0) {
        showError(`Import completed with ${errorCount} error(s). ${successCount} tune(s) imported successfully.`);
        console.error('Import errors:', errors);
      } else {
        showSuccess(`Successfully imported ${successCount} tune(s)!`);
      }

      // Close modal and refresh tunes
      setShowBulkImportModal(false);
      setBulkImportTunes([]);
      setBulkImportHeader('');
      loadTunesContent();
    } catch (error) {
      console.error('Error during bulk import:', error);
      showError('Bulk import failed: ' + error.message);
    } finally {
      setUploading(false);
      setBulkImportProgress({ current: 0, total: 0, status: '' });
    }
  };

  if (loading) {
    return (
      <div className="tunes-container">
        <div className="tunes-header">
          <h1>Musical Notation</h1>
          <p>Loading tune notation pages...</p>
        </div>
        <LoadingSpinner message="Loading tunes..." />
      </div>
    );
  }

  // Calculate pagination
  const indexOfLastTune = currentPage * tunesPerPage;
  const indexOfFirstTune = indexOfLastTune - tunesPerPage;
  const currentTunes = filteredContent.slice(indexOfFirstTune, indexOfLastTune);
  const totalPages = Math.ceil(filteredContent.length / tunesPerPage);

  const handlePageChange = (pageNumber) => {
    shouldScrollToTopRef.current = true;
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (n) => {
    shouldScrollToTopRef.current = true;
    setTunesPerPage(n);
    setCurrentPage(1);
    localStorage.setItem('tunesPerPage', n.toString());
  };

  return (
    <>
      <SEO
        title="Tunes & Sheet Music | Cranford Publications"
        description="Browse thousands of traditional Celtic fiddle tunes with sheet music notation. Scottish, Irish, and Cape Breton fiddle music from Cranford Publications."
        canonical="/tunes"
        robots={hasFilterQuery ? 'noindex, follow' : 'index, follow'}
        keywords="fiddle tunes, sheet music, Scottish tunes, Irish tunes, Cape Breton tunes, traditional music notation, ABC notation"
      />

      <RegisterNudgeBubble />

      <div className="tunes-container">
        <div className="tunes-header-compact" ref={listTopRef}>
          <div className="header-title-row">
            <h1>Tunes & Notations</h1>
            <div className="header-actions">
              {canCreate && (
                <button
                  className="btn btn-primary btn-sm create-tune-btn"
                  onClick={handleCreateNewTune}
                  title="Create a new tune"
                >
                  {getIcon('add')} Create New Tune
                </button>
              )}
              {canCreate && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowImportUrlModal(true)}
                  title="Import tune from URL"
                  style={{ background: '#238636', borderColor: 'rgba(27,31,36,0.15)', color: 'white' }}
                >
                  🌐 Import URL
                </button>
              )}
              {isAdmin && (
                <>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => bulkImportFileInputRef.current?.click()}
                    title="Import multiple ABC tunes from one file"
                  >
                    📚 Single/CD Import ABC
                  </button>
                  <input
                    ref={bulkImportFileInputRef}
                    type="file"
                    accept=".abc,.txt"
                    onChange={handleBulkABCImport}
                    style={{ display: 'none' }}
                  />
                </>
              )}
              {isAdmin && (
                <button
                  className="refresh-btn-compact"
                  onClick={() => loadTunesContent()}
                  title="Refresh tunes list"
                  disabled={loading}
                >
                  🔄
                </button>
              )}
              {(selectedRegions.length > 0 || selectedTuneTypes.length > 0 || showABCOnly || searchTerm || selectedPageType !== 'all' || hiddenTunesMode !== 'public') && (
                <button
                  className="clear-filters-btn"
                  onClick={clearAllFilters}
                  title="Clear all filters"
                >
                  ✕ Clear
                </button>
              )}
            </div>
          </div>
          <div className="search-bar-compact">
            <div className="search-bar-with-count">
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  placeholder="Search tunes, artists, albums..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="search-input"
                />
                <div className="search-icon">🔍</div>
              </div>
              <div className="results-count-inline">
                Showing {filteredContent.length > 0 ? indexOfFirstTune + 1 : 0}-{Math.min(indexOfLastTune, filteredContent.length)} of {filteredContent.length} tunes
              </div>
            </div>
          </div>
        </div>

        <div className="filter-toolbar" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', padding: '0.5rem 0.75rem', background: 'var(--color-surface)', borderRadius: '6px', border: '1px solid var(--color-border)', flexWrap: 'wrap', alignItems: 'center' }}>
          
          <div className="filter-dropdown-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: '1 1 180px' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>Type</label>
            <select 
              className="sort-select"
              style={{ width: '100%', minWidth: '0', padding: '0.4rem 0.6rem', fontSize: '0.85rem', height: '34px', border: '1px solid var(--color-border)', borderRadius: '6px', backgroundColor: 'var(--color-surface)' }}
              value={selectedPageType}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedPageType(val);
                if (val === 'abc-asset') {
                  setShowABCOnly(true);
                } else {
                  setShowABCOnly(false);
                }
              }}
            >
              {pageTypes.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-dropdown-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: '1 1 180px' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>Region</label>
            <select 
              className="sort-select"
              style={{ width: '100%', minWidth: '0', padding: '0.4rem 0.6rem', fontSize: '0.85rem', height: '34px', border: '1px solid var(--color-border)', borderRadius: '6px', backgroundColor: 'var(--color-surface)' }}
              value={selectedRegions.length > 0 ? selectedRegions[0] : ''}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedRegions(val ? [val] : []);
              }}
            >
              <option value="">All Regions</option>
              {getAvailableRegions().map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-dropdown-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: '1 1 180px' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>Tune Type</label>
            <select 
              className="sort-select"
              style={{ width: '100%', minWidth: '0', padding: '0.4rem 0.6rem', fontSize: '0.85rem', height: '34px', border: '1px solid var(--color-border)', borderRadius: '6px', backgroundColor: 'var(--color-surface)' }}
              value={selectedTuneTypes.length > 0 ? selectedTuneTypes[0] : ''}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedTuneTypes(val ? [val] : []);
              }}
            >
              <option value="">All Tune Types</option>
              {getAvailableTuneTypes().map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-dropdown-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: '1 1 180px' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>Artist</label>
            <select 
              className="sort-select"
              style={{ width: '100%', minWidth: '0', padding: '0.4rem 0.6rem', fontSize: '0.85rem', height: '34px', border: '1px solid var(--color-border)', borderRadius: '6px', backgroundColor: 'var(--color-surface)' }}
              value={selectedArtists.length > 0 ? selectedArtists[0] : ''}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedArtists(val ? [val] : []);
              }}
            >
              <option value="">All Artists</option>
              {getAvailableArtists().map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>

          {isAdmin && (
            <div className="filter-dropdown-group filter-hidden-toggle" style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: '1 1 200px', minWidth: '180px' }}>
              <label
                htmlFor="hidden-tunes-mode"
                style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}
              >
                Admin — visibility
              </label>
              <select
                id="hidden-tunes-mode"
                className="sort-select tunes-hidden-filter"
                style={{ width: '100%', minWidth: '0', padding: '0.4rem 0.6rem', fontSize: '0.85rem', height: '34px', border: '1px solid var(--color-border)', borderRadius: '6px', backgroundColor: 'var(--color-surface)' }}
                value={hiddenTunesMode}
                onChange={(e) => setHiddenTunesMode(e.target.value)}
              >
                <option value="public">Public tunes only</option>
                <option value="include">Include hidden tunes</option>
                <option value="only">Hidden only (no notation, no MP3)</option>
              </select>
            </div>
          )}
        </div>

        <div className="tunes-content">
          {/* Content stats moved to search bar */}

          <div className="content-grid">
            {currentTunes.map((item) => (
              <a
                key={item.id}
                href={item.url}
                className={`tune-item ${item.category === 'book-index' ? 'book-index-item' : ''} ${item.featured ? 'featured-item' : ''} ${item.isHidden ? 'tune-item-hidden' : ''}`}
              >
                <div className="item-content">
                  <div className="item-header">
                    <h3 className="item-title">
                      {item.featured && <span style={{ fontSize: '0.9em', marginRight: '6px' }}>⭐</span>}
                      {decodeHtmlEntities(item.title)}
                    </h3>
                    <div className="item-header-badges">
                      <TuneFormatMarker item={item} variant="inline" />
                      {item.category === 'book-index' && (
                        <span className="book-index-badge">Complete Index</span>
                      )}
                      {item.isHidden && (
                        <span className="tune-hidden-badge" title="Hidden from public — no notation or public sample">Hidden</span>
                      )}
                    </div>
                  </div>
                  {/* Tags hidden as per request
                  {item.tags && (
                    <div className="item-tags">
                      <span className="tags-icon">🏷️</span>
                      <div className="tags-list">
                        {(typeof item.tags === 'string'
                          ? item.tags.split(',')
                          : Array.isArray(item.tags)
                            ? item.tags
                            : [item.tags.toString()]
                        ).map((tag, index) => (
                          <span key={index} className="tag">
                            {tag.toString().trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  */}
                  {item.artist && (
                    <div className="item-artist">
                      <span className="artist-icon">🎻</span>
                      <span className="artist-name">{decodeHtmlEntities(item.artist)}</span>
                    </div>
                  )}
                  <p className="item-description">{item.description}</p>
                </div>
              </a>
            ))}
          </div>

          {filteredContent.length === 0 && (
            <div className="no-results">
              <h3>No tunes found</h3>
              <p>Try adjusting your search terms or category filter</p>
            </div>
          )}

          {filteredContent.length > 0 && (
            <PaginationBar
              ariaLabel="Tune list pagination"
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredContent.length}
              indexOfFirst={indexOfFirstTune + 1}
              indexOfLast={indexOfLastTune}
              itemLabel="tunes"
              pageSize={tunesPerPage}
              pageSizeOptions={[25, 50, 75]}
              onPageChange={handlePageChange}
              onPageSizeChange={handleItemsPerPageChange}
            />
          )}
        </div>

        {/* Bulk Import Modal */}
        {showBulkImportModal && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10050
          }}>
            <div className="modal" style={{
              maxWidth: '800px',
              maxHeight: '90vh',
              overflow: 'auto',
              backgroundColor: 'var(--color-background)',
              borderRadius: '8px',
              padding: '20px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <div className="modal-header" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                borderBottom: '2px solid var(--color-border)',
                paddingBottom: '10px'
              }}>
                <h3 style={{ margin: 0, color: 'var(--color-primary)' }}>Single/CD Import ABC Tunes</h3>
                <button
                  className="btn-close"
                  onClick={() => {
                    setShowBulkImportModal(false);
                    setBulkImportTunes([]);
                    setBulkImportHeader('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: 'var(--color-text-secondary)'
                  }}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {bulkImportHeader && (
                  <div style={{
                    marginBottom: '20px',
                    padding: '10px',
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: 'var(--color-text-secondary)',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '150px',
                    overflow: 'auto'
                  }}>
                    <strong>File Header:</strong>
                    <pre style={{ margin: '5px 0 0 0', fontSize: '11px' }}>{bulkImportHeader}</pre>
                  </div>
                )}

                <div style={{ marginBottom: '15px' }}>
                  <strong>Found {bulkImportTunes.length} tune(s) ready to import:</strong>
                </div>

                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  padding: '10px'
                }}>
                  {bulkImportTunes.map((tune, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '12px',
                        marginBottom: '10px',
                        backgroundColor: tune.exists ? 'var(--color-surface)' : 'var(--color-background)',
                        border: `2px solid ${tune.exists ? 'var(--color-secondary)' : 'var(--color-border)'}`,
                        borderRadius: '6px',
                        opacity: tune.exists ? 0.7 : 1
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <strong style={{ color: 'var(--color-primary)' }}>
                            {tune.title}
                          </strong>
                          {tune.exists && (
                            <span style={{
                              marginLeft: '10px',
                              padding: '2px 8px',
                              backgroundColor: 'var(--color-secondary)',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '11px'
                            }}>
                              Already Exists
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleImportTuneFromBulk(tune)}
                          className="btn btn-sm btn-primary"
                          disabled={tune.exists}
                          style={{ marginLeft: '10px' }}
                        >
                          Import
                        </button>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '5px' }}>
                        <div><strong>Type:</strong> {tune.metadata.rhythm_style || 'N/A'}</div>
                        <div><strong>Key:</strong> {tune.metadata.key_signature || tune.metadata.key || 'N/A'}</div>
                        <div><strong>Composer:</strong> {tune.metadata.composer_arranger || 'N/A'}</div>
                        <div><strong>Filename:</strong> {tune.filename}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: '6px',
                  border: '2px solid var(--color-primary)'
                }}>
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Import All Tunes</strong>
                  </div>
                  {bulkImportProgress.total > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{
                        width: '100%',
                        height: '20px',
                        backgroundColor: 'var(--color-border)',
                        borderRadius: '10px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${(bulkImportProgress.current / bulkImportProgress.total) * 100}%`,
                          height: '100%',
                          backgroundColor: 'var(--color-primary)',
                          transition: 'width 0.3s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {bulkImportProgress.current} / {bulkImportProgress.total}
                        </div>
                      </div>
                      <div style={{ marginTop: '5px', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                        {bulkImportProgress.status}
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleImportAllTunes}
                    className="btn btn-primary"
                    disabled={uploading || bulkImportTunes.filter(t => !t.exists).length === 0}
                    style={{ width: '100%' }}
                  >
                    {uploading ? 'Importing...' : `Import All (${bulkImportTunes.filter(t => !t.exists).length} tunes)`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Import URL Modal */}
      {showImportUrlModal && (
        <div className="tune-import-modal-overlay">
          <div className="tune-import-modal">
            <h3>Import Tune from URL</h3>
            <p style={{ marginBottom: '1rem', color: '#8b949e', fontSize: '0.9rem' }}>
              Enter the URL of a Cranford Publications tune page. The system will extract the tune image, audio, and metadata.
            </p>
            <form onSubmit={handleTuneUrlImport}>
              <input
                type="url"
                value={importTuneUrl}
                onChange={(e) => setImportTuneUrl(e.target.value)}
                placeholder="https://www.cranfordpub.ca/tunes/..."
                required
                autoFocus
              />
              <div className="tune-import-actions">
                <button
                  type="button"
                  className="tune-cancel-btn"
                  onClick={() => setShowImportUrlModal(false)}
                  disabled={isUrlImporting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="tune-import-btn"
                  disabled={isUrlImporting}
                >
                  {isUrlImporting ? 'Importing...' : 'Import Tune'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Tunes;
